import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import './Gallery.css';

/* ──────────────────────────────────────────
   Static image manifest — paths only, zero webpack bundling.
   Images are served from public/gallery/ as static files.
   thumbs/  = 400px wide, ~14 KB each  → used in the grid
   medium/  = 1200px wide, ~130 KB each → loaded only when lightbox opens
   ────────────────────────────────────────── */
const TOTAL = 64;
const CATEGORY_CYCLE = ['exterior', 'interior', 'nature', 'activities'];

const galleryImages = Array.from({ length: TOTAL }, (_, i) => {
  const n    = i + 1;
  const name = `cabana piatra buru-${n}.jpg`;
  return {
    id:       i,
    num:      n,
    thumb:    `/gallery/thumbs/${name}`,
    medium:   `/gallery/medium/${name}`,
    category: CATEGORY_CYCLE[i % CATEGORY_CYCLE.length],
  };
});

/* ──────────────────────────────────────────
   Firefly particle generator
   ────────────────────────────────────────── */
function generateParticles(count) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: 2 + Math.random() * 3,
    duration: 4 + Math.random() * 8,
    delay: Math.random() * 6,
    dx: (Math.random() - 0.5) * 60,
    dy: -(20 + Math.random() * 60),
    type: ['gold', 'green', 'red'][Math.floor(Math.random() * 3)],
  }));
}

const heroParticles = generateParticles(12);
const fireflyParticles = generateParticles(20);

/* ══════════════════════════════════════════
   LAZY IMAGE — only loads when near viewport
   ══════════════════════════════════════════ */
const LazyImage = memo(function LazyImage({ thumb, alt, onReveal }) {
  const imgRef  = useRef(null);
  const [visible, setVisible] = useState(false);
  const [loaded,  setLoaded]  = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
          onReveal?.();
        }
      },
      { rootMargin: '200px 0px' }   // start loading 200px before entering viewport
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [onReveal]);

  return (
    <div ref={imgRef} className="gallery__img-wrap">
      {/* Skeleton shown until image loaded */}
      {!loaded && <div className="gallery__skeleton" style={{ paddingBottom: '75%' }} />}
      {visible && (
        <img
          className={`gallery__item-img${loaded ? ' gallery__item-img--loaded' : ''}`}
          src={thumb}
          alt={alt}
          decoding="async"
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
});

/* ══════════════════════════════════════════
   GALLERY COMPONENT
   ══════════════════════════════════════════ */
export default function Gallery() {
  const { t } = useLanguage();

  const [activeFilter, setActiveFilter] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [lbLoaded, setLbLoaded] = useState(false);   // lightbox medium-res loaded

  /* ── Filter logic ── */
  const FILTERS = useMemo(
    () => [
      { key: 'all', label: t('gallery.filters.all') },
      { key: 'exterior', label: t('gallery.filters.exterior') },
      { key: 'interior', label: t('gallery.filters.interior') },
      { key: 'nature', label: t('gallery.filters.nature') },
      { key: 'activities', label: t('gallery.filters.activities') },
    ],
    [t]
  );

  const filteredImages = useMemo(
    () =>
      activeFilter === 'all'
        ? galleryImages
        : galleryImages.filter((img) => img.category === activeFilter),
    [activeFilter]
  );

  /* ── Lightbox navigation ── */
  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLbLoaded(false);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    setLbLoaded(false);
    document.body.style.overflow = '';
  }, []);

  const goPrev = useCallback(() => {
    setLbLoaded(false);
    setLightboxIndex((prev) =>
      prev === 0 ? filteredImages.length - 1 : prev - 1
    );
  }, [filteredImages.length]);

  const goNext = useCallback(() => {
    setLbLoaded(false);
    setLightboxIndex((prev) =>
      prev === filteredImages.length - 1 ? 0 : prev + 1
    );
  }, [filteredImages.length]);

  /* ── Keyboard controls ── */
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, closeLightbox, goPrev, goNext]);

  /* ── Cleanup on unmount ── */
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  /* ── Scroll-reveal observer ── */
  useEffect(() => {
    const reveals = document.querySelectorAll('[data-reveal]');
    if (!reveals.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('revealed');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -40px 0px' }
    );
    reveals.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="gallery">
      {/* ═══ HERO ═══ */}
      <section className="gallery__hero">

        {/* ── Full-bleed background photo with Ken Burns effect ── */}
        <div className="gallery__hero-bg" aria-hidden="true">
          <img
            className="gallery__hero-bg-img"
            src="/gallery/medium/cabana piatra buru-7.jpg"
            alt=""
            fetchpriority="high"
          />
          {/* Dark gradient veil over the photo */}
          <div className="gallery__hero-veil" />
          {/* Fine noise texture */}
          <div className="gallery__hero-grain" />
        </div>

        {/* ── Floating gold particles ── */}
        <div className="gallery__hero-particles" aria-hidden="true">
          {heroParticles.map((p) => (
            <span
              key={p.id}
              className={`gallery__hero-particle gallery__hero-particle--${p.type}`}
              style={{
                left: p.left,
                top: p.top,
                width: p.size,
                height: p.size,
                animationDuration: `${p.duration}s`,
                animationDelay: `${p.delay}s`,
                '--dx': `${p.dx}px`,
                '--dy': `${p.dy}px`,
              }}
            />
          ))}
        </div>

        {/* ── Two-column layout ── */}
        <div className="gallery__hero-layout">

          {/* LEFT — text content */}
          <div className="gallery__hero-content" data-reveal>

            {/* Eyebrow label */}
            <div className="gallery__hero-label">
              <span className="gallery__hero-label-line" />
              {t('gallery.hero.label')}
              <span className="gallery__hero-label-line" />
            </div>

            <h1 className="gallery__hero-title">
              {t('gallery.hero.titleStart')}{' '}
              <em>{t('gallery.hero.titleAccent')}</em>
            </h1>

            <p className="gallery__hero-subtitle">
              {t('gallery.hero.subtitle')}
            </p>

            {/* Stat chips */}
            <div className="gallery__hero-stats">
              <div className="gallery__hero-stat">
                <span className="gallery__hero-stat-value">{galleryImages.length}</span>
                <span className="gallery__hero-stat-label">{t('gallery.hero.statPhotos')}</span>
              </div>
              <div className="gallery__hero-stat">
                <span className="gallery__hero-stat-value">4</span>
                <span className="gallery__hero-stat-label">{t('gallery.hero.statCategories')}</span>
              </div>
              <div className="gallery__hero-stat">
                <span className="gallery__hero-stat-value">HD</span>
                <span className="gallery__hero-stat-label">{t('gallery.hero.statQuality')}</span>
              </div>
            </div>

            {/* Decorative ornament */}
            <div className="gallery__hero-ornament" aria-hidden="true">
              <span className="gallery__hero-ornament-line" />
              <span className="gallery__hero-ornament-diamond" />
              <span className="gallery__hero-ornament-line gallery__hero-ornament-line--short" />
              <span className="gallery__hero-ornament-diamond gallery__hero-ornament-diamond--sm" />
              <span className="gallery__hero-ornament-line gallery__hero-ornament-line--short" />
              <span className="gallery__hero-ornament-diamond" />
              <span className="gallery__hero-ornament-line" />
            </div>
          </div>

          {/* RIGHT — highlights panel */}
          <div className="gallery__hero-highlights">
            {[
              {
                mod: '1',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 17l4-8 4 5 3-3 4 6H3z" />
                    <circle cx="17" cy="7" r="2" />
                  </svg>
                ),
                titleKey: 'gallery.hero.feat1Title',
                descKey:  'gallery.hero.feat1Desc',
              },
              {
                mod: '2',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                ),
                titleKey: 'gallery.hero.feat2Title',
                descKey:  'gallery.hero.feat2Desc',
              },
              {
                mod: '3',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4l3 3" />
                    <path d="M5.2 5.2l1.4 1.4M17.4 5.2l-1.4 1.4M5.2 18.8l1.4-1.4M17.4 18.8l-1.4-1.4" />
                  </svg>
                ),
                titleKey: 'gallery.hero.feat3Title',
                descKey:  'gallery.hero.feat3Desc',
              },
              {
                mod: '4',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s-8-4.5-8-11.8A8 8 0 0112 2a8 8 0 018 8.2c0 7.3-8 11.8-8 11.8z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                ),
                titleKey: 'gallery.hero.feat4Title',
                descKey:  'gallery.hero.feat4Desc',
              },
            ].map(({ mod, icon, titleKey, descKey }) => (
              <div key={mod} className={`gallery__hero-hl-card gallery__hero-hl-card--${mod}`}>
                <div className="gallery__hero-hl-icon">{icon}</div>
                <div className="gallery__hero-hl-body">
                  <h3 className="gallery__hero-hl-title">{t(titleKey)}</h3>
                  <p className="gallery__hero-hl-desc">{t(descKey)}</p>
                </div>
                <div className="gallery__hero-hl-glow" />
              </div>
            ))}
          </div>

        </div>

        {/* Scroll indicator */}
        <div className="gallery__hero-scroll">
          <span className="gallery__hero-scroll-line" />
          {t('gallery.hero.scroll')}
        </div>

      </section>

      {/* ═══ MAIN GALLERY ═══ */}
      <section className="gallery__main">
        {/* Ambient blobs */}
        <div className="gallery__ambient" aria-hidden="true">
          <div className="gallery__ambient-blob gallery__ambient-blob--1" />
          <div className="gallery__ambient-blob gallery__ambient-blob--2" />
          <div className="gallery__ambient-blob gallery__ambient-blob--3" />
          <div className="gallery__ambient-blob gallery__ambient-blob--4" />
        </div>

        {/* Fireflies */}
        <div className="gallery__fireflies" aria-hidden="true">
          {fireflyParticles.map((f) => (
            <span
              key={f.id}
              className="gallery__firefly"
              style={{
                left: f.left,
                top: f.top,
                width: f.size,
                height: f.size,
                animationDuration: `${f.duration}s`,
                animationDelay: `${f.delay}s`,
                '--fx': `${f.dx}px`,
                '--fy': `${f.dy}px`,
              }}
            />
          ))}
        </div>

        <div className="gallery__inner">
          {/* Ornament divider */}
          <div className="gallery__divider" data-reveal>
            <span className="gallery__divider-line" />
            <span className="gallery__divider-diamond" />
            <span className="gallery__divider-line gallery__divider-line--short" />
            <span className="gallery__divider-diamond gallery__divider-diamond--sm" />
            <span className="gallery__divider-line gallery__divider-line--short" />
            <span className="gallery__divider-diamond" />
            <span className="gallery__divider-line" />
          </div>

          {/* Filter bar */}
          <div className="gallery__filters" data-reveal>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                className={`gallery__filter-btn ${
                  activeFilter === f.key ? 'gallery__filter-btn--active' : ''
                }`}
                onClick={() => setActiveFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Photo count */}
          <p className="gallery__count" data-reveal>
            {t('gallery.showing')}{' '}
            <strong>{filteredImages.length}</strong>{' '}
            {t('gallery.photos')}
          </p>

          {/* Masonry grid */}
          {filteredImages.length > 0 ? (
            <div className="gallery__grid">
              {filteredImages.map((img, index) => (
                <div
                  key={img.id}
                  className="gallery__item"
                  style={{ animationDelay: `${(index % 12) * 0.06}s` }}
                  onClick={() => openLightbox(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && openLightbox(index)}
                >
                  <LazyImage
                    thumb={img.thumb}
                    alt={`Cabana Piatra Buru — ${img.num}`}
                  />

                  {/* Hover overlay */}
                  <div className="gallery__item-overlay">
                    <span className="gallery__item-number">
                      {String(index + 1).padStart(2, '0')} / {String(filteredImages.length).padStart(2, '0')}
                    </span>
                    <span className="gallery__item-caption">
                      Cabana Piatra Buru
                    </span>
                  </div>

                  {/* Expand icon */}
                  <span className="gallery__item-expand" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 3 21 3 21 9" />
                      <polyline points="9 21 3 21 3 15" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="gallery__empty">
              <div className="gallery__empty-icon">📷</div>
              <h3 className="gallery__empty-title">{t('gallery.emptyTitle')}</h3>
              <p className="gallery__empty-text">{t('gallery.emptyText')}</p>
            </div>
          )}
        </div>
      </section>

      {/* ═══ BOTTOM CTA ═══ */}
      <section className="gallery__cta">
        <div className="gallery__cta-glow" />
        <div className="gallery__cta-content" data-reveal>
          <h2 className="gallery__cta-title">
            {t('gallery.cta.titleStart')}{' '}
            <em>{t('gallery.cta.titleAccent')}</em>
          </h2>
          <p className="gallery__cta-text">{t('gallery.cta.text')}</p>
          <Link to="/rent" className="gallery__cta-btn">
            {t('gallery.cta.button')}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </div>
      </section>

      {/* ═══ LIGHTBOX ═══ */}
      {lightboxIndex !== null &&
        createPortal(
          <div
            className="gallery__lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Photo lightbox"
          >
            <div
              className="gallery__lightbox-backdrop"
              onClick={closeLightbox}
            />

            {/* Close */}
            <button
              className="gallery__lightbox-close"
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              ✕
            </button>

            {/* Prev */}
            <button
              className="gallery__lightbox-nav gallery__lightbox-nav--prev"
              onClick={goPrev}
              aria-label="Previous photo"
            >
              ‹
            </button>

            {/* Image */}
            <div className="gallery__lightbox-content">
              {/* Show thumb instantly, swap to medium once loaded */}
              <div className="gallery__lightbox-img-wrap">
                {/* Blurred thumb shown immediately as placeholder */}
                <img
                  className="gallery__lightbox-img gallery__lightbox-img--thumb"
                  src={filteredImages[lightboxIndex]?.thumb}
                  alt=""
                  aria-hidden="true"
                />
                {/* Medium-res loaded in background, fades in when ready */}
                <img
                  key={filteredImages[lightboxIndex]?.medium}
                  className={`gallery__lightbox-img gallery__lightbox-img--full${lbLoaded ? ' gallery__lightbox-img--ready' : ''}`}
                  src={filteredImages[lightboxIndex]?.medium}
                  alt={`Cabana Piatra Buru — ${lightboxIndex + 1}`}
                  onLoad={() => setLbLoaded(true)}
                />
                {/* Spinner shown while medium loads */}
                {!lbLoaded && <div className="gallery__lightbox-spinner" />}
              </div>

              <div className="gallery__lightbox-info">
                <span className="gallery__lightbox-counter">
                  {lightboxIndex + 1} / {filteredImages.length}
                </span>
                <span className="gallery__lightbox-sep" />
                <span className="gallery__lightbox-label">
                  Cabana Piatra Buru
                </span>
              </div>
            </div>

            {/* Next */}
            <button
              className="gallery__lightbox-nav gallery__lightbox-nav--next"
              onClick={goNext}
              aria-label="Next photo"
            >
              ›
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}
